import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { LetDirective } from '@ngrx/component';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { AccountUsersModule } from '@capital-access/account/users';
import { BnyRepository } from '@capital-access/bny/common';
import { CommonFormattingModule } from '@capital-access/common/formatting';
import { CommonLocalizationModule } from '@capital-access/common/localization';
import {
  FireflyAlertsModule,
  FireflyChipModule,
  FireflyDrawerModule,
  FireflyDropdownModule,
  FireflyEmptyStatesModule,
  FireflyListModule,
  FireflyPopoverModule,
  FireflySkeletonDrawerLoaderModule
} from '@capital-access/firefly/components';
import { TEMPLATE_DESIGNER_FEATURE_KEY, TEMPLATE_LIBRARY_FEATURE_KEY } from '@capital-access/reporting/common';
import { ReportDataService, TemplateLibraryService } from '@capital-access/reporting/common';
import { ReportTypeReportSections } from '@capital-access/reporting/report-section';
import { templateDesignerReducer } from '@capital-access/reporting/template-designer';
import { templateLibraryReducer } from '@capital-access/reporting/template-library';
import { DrawerService, SavedTemplateListEffects, TemplateService } from '@capital-access/reporting/template-library';
import { BnyManagementReportEffects } from './bny-management-report/+state/bny-management-report.effects';
import { ModifyReportDrawerBodyComponent } from './components/report-drawer/modify-report-drawer-body.component';
import { ModifyReportDrawerFooterComponent } from './components/report-drawer/modify-report-drawer-footer.component';
import { SectionsSelectorComponent } from './components/sections-selector/sections-selector.component';
import { SetAsDefaultToggleComponent } from './components/set-as-default-toggle/set-as-default-toggle.component';
import { ConfigurableItineraryReportEffects } from './configurable-itinerary/+state/configurable-itinerary-report.effects';
import { ConfigurableProfileReportEffects } from './configurable-profile/+state/configurable-profile-report.effects';
import { LOCALIZATION_KEY } from './constants';
import { CreateReportEffects } from './core/+state/report.effects';
import { CreateReportService } from './core/services/create-report.service';
import { FileNameService } from './core/services/file-name.service';
import { HistoricalOwnershipDrawerEffects } from './historical-ownership/+state/historical-ownership-drawer.effects';
import { HistoricalOwnershipReportEffects } from './historical-ownership/+state/historical-ownership-report.effects';
import { HistoricalOwnershipReportDrawerComponent } from './historical-ownership/components/historical-ownership-report-drawer/historical-ownership-report-drawer.component';
import { ItineraryDrawerEffects } from './itinerary/+state/itinerary-drawer.effects';
import { ItineraryReportEffects } from './itinerary/+state/itinerary-report.effects';
import { ConfigurableItineraryTemplatesComponent } from './itinerary/components/configurable-itinenary-templates/configurable-itinerary-templates.component';
import { ItineraryReportDrawerComponent } from './itinerary/components/itinerary-report-drawer/itinerary-report-drawer.component';
import { ItineraryReportSectionsComponent } from './itinerary/components/itinerary-report-sections/itinerary-report-sections.component';
import { OwnershipAnalysisReportEffects } from './ownership-analysis-report/+state/ownership-analysis-report.effects';
import { ProfileReportDrawerEffects } from './profile/+state/profile-drawer.effects';
import { ProfileReportEffects } from './profile/+state/profile-report.effects';
import { ConfigurableTemplatesComponent } from './profile/components/configurable-templates/configurable-templates.component';
import { ProfileReportDrawerComponent } from './profile/components/profile-report-drawer/profile-report-drawer.component';
import { ProfileReportNamingService } from './profile/services/profile-report-naming.service';

@NgModule({
  imports: [
    CommonModule,
    CommonLocalizationModule.forFeature(LOCALIZATION_KEY),
    CommonFormattingModule,
    StoreModule.forFeature(TEMPLATE_LIBRARY_FEATURE_KEY, templateLibraryReducer),
    StoreModule.forFeature(TEMPLATE_DESIGNER_FEATURE_KEY, templateDesignerReducer),
    EffectsModule.forFeature([
      ProfileReportEffects,
      ProfileReportDrawerEffects,
      ItineraryReportEffects,
      HistoricalOwnershipReportEffects,
      HistoricalOwnershipDrawerEffects,
      CreateReportEffects,
      ItineraryDrawerEffects,
      ConfigurableProfileReportEffects,
      BnyManagementReportEffects,
      OwnershipAnalysisReportEffects,
      ConfigurableItineraryReportEffects,
      SavedTemplateListEffects
    ]),
    FireflyListModule,
    FireflyDrawerModule,
    FireflyDropdownModule,
    ReactiveFormsModule,
    LetDirective,
    FireflyChipModule,
    FireflyAlertsModule,
    AccountUsersModule,
    FireflySkeletonDrawerLoaderModule,
    FireflyEmptyStatesModule,
    FireflyPopoverModule
  ],
  providers: [
    CreateReportService,
    ProfileReportNamingService,
    FileNameService,
    TemplateLibraryService,
    DrawerService,
    TemplateService,
    ReportDataService,
    ReportTypeReportSections,
    BnyRepository
  ],
  declarations: [
    SectionsSelectorComponent,
    ModifyReportDrawerBodyComponent,
    HistoricalOwnershipReportDrawerComponent,
    ModifyReportDrawerFooterComponent,
    SetAsDefaultToggleComponent,
    ProfileReportDrawerComponent,
    ItineraryReportDrawerComponent,
    ItineraryReportSectionsComponent,
    ConfigurableTemplatesComponent,
    ConfigurableItineraryTemplatesComponent
  ]
})
export class CreateReportModule {}
